import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import useId from '@accessible/use-id';
import { getSizeToken, getVariantToken, getFontTokenStyle, spacing, rem } from '@koaly/mixins';
import { withExtra } from '@koaly/with-extra';
import React from 'react';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { useLinkWrapper } from '../../hooks/useLinkWrapper.js';
import { Label } from '../Label/Label.js';
import { Link } from '../Link/Link.js';
import { Paragraph } from '../Paragraph/Paragraph.js';
import { StatusMessages } from '../StatusMessages/StatusMessages.js';

const StyledInput = styled.input `
  /* reset */
  margin: 0;
  outline: none;
  outline-offset: unset;
  box-sizing: border-box;
  border: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  /* styling */
  width: 100%;
  border-radius: ${({ _size }) => getSizeToken('input', 'borderRadius', _size)};

  padding: ${({ _size }) => getSizeToken('input', 'mobilePadding', _size)};

  ${up('s')} {
    padding: ${({ _size }) => getSizeToken('input', 'padding', _size)}
  }


  ${({ variant, elevation, theme }) => css `
    color: ${getVariantToken('input', 'color', variant)};
    background-color: ${getVariantToken('input', 'backgroundColor', variant)};
    box-shadow: inset 0 0 0 1px ${getVariantToken('input', 'borderColor', variant)}
      ${elevation && elevation !== 'none' && `,${theme.elevations[elevation]}`};

    &:focus, &.focus {
      box-shadow: inset 0 0 0 1px ${getVariantToken('input', 'borderColorFocus', variant)}
        ${elevation && elevation !== 'none' && `,${theme.elevations[elevation]}`};;
    }

    &::placeholder {
      color: ${getVariantToken('input', 'placeholderColor', variant)};
    }
  `};

  ${({ hasError, variant, theme, elevation }) => hasError && css `
    box-shadow: inset 0 0 0 1px ${getVariantToken('input', 'errorBorderColor', variant)}
     ${elevation && elevation !== 'none' && `,${theme.elevations[elevation]}`} !important;
  `};

  ${getFontTokenStyle('label', 'medium')};
`;
const StyledLabelContainer = styled.div `
  display: block;
  width: 100%;
  ${({ spaceAfter }) => spaceAfter && `margin-bottom: ${spacing(spaceAfter)};`};
`;
const StyledParagraph = styled(Paragraph) `
  padding-top: ${rem(12)};
`;
const StyledLabelRightLink = styled(Link) `
  float: right;
`;
const Input = withExtra((_a, ref) => {
    var { type, defaultValue, value, onChange, placeholder, variant, id, label, spaceAfter, name, elevation, errors, hasError, hideLabel, labelRightLink, tabIndex, size, description } = _a, rest = __rest(_a, ["type", "defaultValue", "value", "onChange", "placeholder", "variant", "id", "label", "spaceAfter", "name", "elevation", "errors", "hasError", "hideLabel", "labelRightLink", "tabIndex", "size", "description"]);
    const inputId = useId(id || name, 'input-');
    const linkWrap = useLinkWrapper();
    return (React.createElement(StyledLabelContainer, { spaceAfter: spaceAfter },
        !hideLabel && React.createElement(Label, { htmlFor: inputId }, label),
        labelRightLink && linkWrap(React.createElement(StyledLabelRightLink, { href: labelRightLink.href, tabIndex: labelRightLink.tabIndex, size: "small", onClick: labelRightLink.onClick }, labelRightLink.label)),
        React.createElement(StyledInput, Object.assign({ id: inputId, type: type, ref: ref, _size: size, value: value, defaultValue: defaultValue, elevation: elevation, onChange: onChange, placeholder: placeholder, hasError: !!errors || hasError, variant: variant, name: name, "aria-label": hideLabel && label, "aria-invalid": !!errors, "aria-describedby": errors ? `${inputId}-error` : undefined, tabIndex: tabIndex }, rest)),
        description && (React.createElement(StyledParagraph, { size: "small" }, description)),
        errors && (React.createElement(StatusMessages, { spaceBefore: 6, type: "danger", id: `${inputId}-error`, items: typeof errors === 'string' ?
                errors.replace('{label}', label) :
                errors.map(x => x.replace('{label}', label)) }))));
}, { memo: true, name: 'Input' });
Input.defaultProps = {
    variant: 'primary',
    elevation: 1,
    hideLabel: false,
    size: 'medium',
};

export { Input, StyledInput };
//# sourceMappingURL=Input.js.map
